import { Stack } from '@mui/system';
import { isNull } from 'lodash-es';
import React, { useMemo } from 'react';
import { useGlobalFeatureFlags } from '@/hooks/useGlobalFeatureFlags';
import { useIsMobile } from '@/hooks/useIsMobile';
import RequiredLabel from '@/modules/form/components/RequiredLabel';
import CommonSearchInput, {
  CommonSearchInputProps,
} from '@/modules/search/components/CommonSearchInput';

export interface ClientTextSearchInputProps extends CommonSearchInputProps {
  showSearchTips?: boolean;
  errorMessage?: string;
  label?: string | null;
}

// NOTE: we should use translations for variations (like adding MCI ID) but its not set up yet for multiple envs

const defaultSearchClientsPlaceholder =
  'Search by name, DOB, SSN, or Personal ID';

// NOTE: description should match actual behavior of ClientSearch concern in the warehouse
const defaultSearchClientsHelperText =
  'Search by name, DOB (mm/dd/yyyy), SSN (xxx-yy-zzzz), Warehouse ID, or Personal ID.';
const defaultSearchTips =
  'It is often most efficient to search using the first few characters of the first name and last name, e.g. to find Jane Smith you might search for ja sm.';
const getDefaultPlaceholderAndHelper = (mciEnabled: boolean) => {
  if (!mciEnabled) {
    return {
      placeholder: defaultSearchClientsPlaceholder,
      helper: defaultSearchClientsHelperText,
    };
  } else {
    return {
      placeholder: defaultSearchClientsPlaceholder.replace(
        'or Personal ID',
        'Personal ID, or MCI ID'
      ),
      helper: defaultSearchClientsHelperText.replace(
        'or Personal ID',
        'Personal ID, or MCI ID'
      ),
    };
  }
};

const ClientTextSearchInput: React.FC<ClientTextSearchInputProps> = ({
  showSearchTips = false,
  errorMessage,
  helperText: helperTextProp,
  label = 'Search Clients',
  ...props
}) => {
  const { globalFeatureFlags } = useGlobalFeatureFlags();
  const isTiny = useIsMobile('sm');
  const [placeholder, helperText] = useMemo(() => {
    const { placeholder, helper } = getDefaultPlaceholderAndHelper(
      !!globalFeatureFlags?.mciIdEnabled
    );
    // Construct helper text.
    // Even if helperText passed is `null`, we still need to show the `errorMessage` if present.
    // We hide helper text on tiny screens
    const helperTextElement =
      helperTextProp || isNull(helperTextProp) ? helperTextProp : <>{helper}</>;
    const helperTextNode = showSearchTips ? (
      <>
        <Stack
          sx={{ mt: 1.5, px: 1, typography: 'body2' }}
          component='span'
          gap={2}
        >
          {errorMessage && <b>{errorMessage}</b>}
          {!isTiny && (
            <span>
              <b>To add a client,</b> search first. <b>Search Tips:</b>{' '}
              {helperTextElement} <span>{defaultSearchTips}</span>
            </span>
          )}
        </Stack>
      </>
    ) : (
      <Stack gap={0.5} component='span'>
        {errorMessage && <span>{errorMessage}</span>}
        {!isTiny && <span>{helperTextElement}</span>}
      </Stack>
    );

    return [placeholder, helperTextNode];
  }, [
    errorMessage,
    globalFeatureFlags,
    helperTextProp,
    showSearchTips,
    isTiny,
  ]);

  return (
    <CommonSearchInput
      label={
        label && (
          <RequiredLabel text={label} TypographyProps={{ fontWeight: 600 }} />
        )
      }
      name='search client'
      placeholder={placeholder}
      helperText={helperText}
      {...props}
      InputProps={{
        ...props.InputProps,
        inputProps: {
          'aria-label': label ? undefined : 'search clients',
          'data-testid': 'clientSearchInput',
          ...props.InputProps?.inputProps,
        },
      }}
    />
  );
};

export default ClientTextSearchInput;
